import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import usePageTitle from 'hooks/usePageTitle';
import useEscapeClose from 'hooks/useEscapeClose';

const CATEGORIES = [
  { value: 'courrier', label: 'Courrier' },
  { value: 'cr', label: 'Compte rendu' },
  { value: 'contrat', label: 'Contrat' },
  { value: 'facture', label: 'Facture' },
  { value: 'autre', label: 'Autre' },
];

const EMPTY = { titre: '', categorie: 'autre', description: '', date_document: '', mots_cles: '' };

export default function Archivage() {
  usePageTitle('Archivage');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({ categorie: '', search: '' });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEscapeClose(showForm, () => setShowForm(false));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [listRes, statsRes] = await Promise.all([
        api.get('/assistanat/archives', { params }),
        api.get('/assistanat/archives/stats'),
      ]);
      setItems(listRes.data);
      setStats(statsRes.data);
    } catch {} finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titre) return alert('Titre requis');
    setSaving(true);
    try {
      const payload = {
        ...form,
        mots_cles: form.mots_cles ? form.mots_cles.split(',').map(m => m.trim()).filter(Boolean) : [],
      };
      if (editId) {
        await api.put(`/assistanat/archives/${editId}`, payload);
      } else {
        await api.post('/assistanat/archives', payload);
      }
      setShowForm(false);
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleEdit = (item) => {
    setForm({
      titre: item.titre || '', categorie: item.categorie || 'autre',
      description: item.description || '', date_document: item.date_document?.split('T')[0] || '',
      mots_cles: (item.mots_cles || []).join(', '),
    });
    setEditId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette archive ?')) return;
    try {
      await api.delete(`/assistanat/archives/${id}`);
      load();
    } catch {}
  };

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const setFld = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const catLabel = (c) => CATEGORIES.find(x => x.value === c)?.label || c;
  const catColor = (c) => {
    const colors = { courrier: '#E3F2FD', cr: '#F3E5F5', contrat: '#E8F5E9', facture: '#FFF3E0', autre: '#F5F5F5' };
    const textColors = { courrier: '#1565C0', cr: '#7B1FA2', contrat: '#2E7D32', facture: '#F57C00', autre: '#616161' };
    return { background: colors[c] || '#F5F5F5', color: textColors[c] || '#616161' };
  };

  return (
    <div>
<div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Total</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg></div></div>
          <div className="stat-card-value">{stats.total || 0}</div>
          <div className="stat-card-sub">documents archives</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Courriers</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></div></div>
          <div className="stat-card-value" style={{ color: '#1565C0' }}>{stats.courrier || 0}</div>
          <div className="stat-card-sub">archives</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Comptes rendus</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></div></div>
          <div className="stat-card-value" style={{ color: '#7B1FA2' }}>{stats.cr || 0}</div>
          <div className="stat-card-sub">archives</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Contrats</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></div></div>
          <div className="stat-card-value" style={{ color: '#2E7D32' }}>{stats.contrat || 0}</div>
          <div className="stat-card-sub">archives</div>
        </div>
      </div>

      <div className="content-card">
        <div className="gss-toolbar">
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <input className="form-input" placeholder="Rechercher (titre, mots-cles)..." value={filters.search}
              onChange={e => setF('search', e.target.value)} style={{ maxWidth: 250 }} />
            <select className="form-input" value={filters.categorie} onChange={e => setF('categorie', e.target.value)} style={{ maxWidth: 160 }}>
              <option value="">Toutes categories</option>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <button className="gss-btn gss-btn-primary" onClick={() => { setForm(EMPTY); setEditId(null); setShowForm(true); }}>
            + Archiver un document
          </button>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
        ) : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Titre</th>
                  <th>Categorie</th>
                  <th>Date document</th>
                  <th>Mots-cles</th>
                  <th>Date archivage</th>
                  <th style={{ width: 90 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan="7" style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>Aucune archive</td></tr>
                )}
                {items.map(item => (
                  <tr key={item.id}>
                    <td><strong>{item.reference}</strong></td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.titre}</td>
                    <td>
                      <span className="gss-badge" style={catColor(item.categorie)}>{catLabel(item.categorie)}</span>
                    </td>
                    <td>{item.date_document ? new Date(item.date_document).toLocaleDateString('fr-FR') : '-'}</td>
                    <td>
                      {item.mots_cles && item.mots_cles.length > 0 ? (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {item.mots_cles.slice(0, 3).map((m, i) => (
                            <span key={i} className="gss-badge gss-badge-grey" style={{ fontSize: '0.7rem' }}>{m}</span>
                          ))}
                          {item.mots_cles.length > 3 && <span style={{ fontSize: '0.7rem', color: '#9CA3AF' }}>+{item.mots_cles.length - 3}</span>}
                        </div>
                      ) : '-'}
                    </td>
                    <td>{new Date(item.created_at).toLocaleDateString('fr-FR')}</td>
                    <td>
                      <div className="gss-actions">
                        <button className="gss-btn-icon" onClick={() => handleEdit(item)} title="Modifier">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="gss-btn-icon gss-btn-icon--delete" onClick={() => handleDelete(item.id)} title="Archiver">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* FORMULAIRE MODAL */}
      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
                <h2>{editId ? 'Modifier l\'archive' : 'Archiver un document'}</h2>
                <div className="gss-modal-subtitle">Gestion documentaire</div>
              </div>
              <button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
                  </svg>
                  <span>IDENTIFICATION DU DOCUMENT</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field gss-field-full">
                    <label className="form-label">Titre *</label>
                    <input className="form-input" value={form.titre} onChange={e => setFld('titre', e.target.value)} required />
                  </div>
                  <div className="gss-field">
                    <label className="form-label">Categorie</label>
                    <select className="form-input" value={form.categorie} onChange={e => setFld('categorie', e.target.value)}>
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="gss-field">
                    <label className="form-label">Date du document</label>
                    <input className="form-input" type="date" value={form.date_document} onChange={e => setFld('date_document', e.target.value)} />
                  </div>
                </div>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>
                  </svg>
                  <span>DETAILS & INDEXATION</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field gss-field-full">
                    <label className="form-label">Description</label>
                    <textarea className="form-input" rows="3" value={form.description} onChange={e => setFld('description', e.target.value)} style={{ resize: 'vertical' }} />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label className="form-label">Mots-cles (separes par des virgules)</label>
                    <input className="form-input" value={form.mots_cles} onChange={e => setFld('mots_cles', e.target.value)}
                      placeholder="Ex: securite, contrat, 2026, client-X" />
                  </div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>
                  {saving ? 'Enregistrement...' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4z"/></svg> Enregistrer</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
